import { InformationCircleIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import {
  AssignmentList,
  type AssignmentRowStatus,
} from '@/components/ui/assignment-list';
import { Button } from '@/components/ui/button';
import { InfoCallout } from '@/components/ui/info-callout';
import { CredentialType } from '@/sections/asset/components/tabs/credentials/credentialForms';
import type {
  SeedStepProps,
  WizardCredentialData,
} from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';
import { SeedTypeOption } from '@/types';

import { parseSeedLines, recorderAppCount } from './credentialAssign.util';
import { CredentialConfigModal } from './CredentialConfigModal';
import {
  collectFieldErrors,
  createInitialCredentialData,
} from './credentialStep.util';

// Option B: in the Shared editor RECIPE is present-but-disabled — a recipe is a
// hand-scripted, host-specific HTTP flow, so it can only be authored per-app via
// Custom. The Custom editor passes no disabledTypes (RECIPE selectable there).
const SHARED_DISABLED_TYPES: Partial<Record<CredentialType, string>> = {
  [CredentialType.RECIPE]:
    'Manual Login Flow is app-specific — configure it per application with Custom.',
};

/**
 * CredentialAssignStep — the multi-app credential step (seedCount > 1 web seeds
 * in `configure-multi` mode). Renders one {@link AssignmentList} row per parsed
 * app URL with an Off/Shared/Custom mode control and an expandable per-app
 * editor, a "Configure shared" card, a bulk "Set all" control, and a recorder
 * tally. Every secret threads through `setFormData` (P2-2) — only non-secret UI
 * state (`expandedId`, `sharedExpanded`) is local.
 */
// What the CredentialConfigModal is currently configuring (or null = closed).
type ConfigTarget = { kind: 'shared' } | { kind: 'custom'; url: string } | null;

export function CredentialAssignStep({ formData, setFormData }: SeedStepProps) {
  // Non-secret UI state only (R-9). Secrets live in formData.credential.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [openConfig, setOpenConfig] = useState<ConfigTarget>(null);

  // Defensive: shouldSkip already gates this step to configure-multi.
  if (formData.credential.mode !== 'configure-multi') return null;

  const { shared, perApp, custom } = formData.credential;
  const isWebService = formData.seedType === SeedTypeOption.WEB_SERVICE;
  const appUrls = parseSeedLines(formData.textInput);

  // Writes the configure-multi slice immutably; bails if the mode drifted.
  const updateMulti = (
    patch: Partial<{
      shared: WizardCredentialData | null;
      perApp: Record<string, (typeof perApp)[string]>;
      custom: Record<string, WizardCredentialData>;
    }>
  ) =>
    setFormData(prev => {
      if (prev.credential.mode !== 'configure-multi') return prev;
      return { ...prev, credential: { ...prev.credential, ...patch } };
    });

  const handleModeChange = (url: string, next: (typeof perApp)[string]) => {
    const nextPerApp = { ...perApp, [url]: next };
    // On transition to Custom, seed the per-app bundle (seeds recipeDraft.url
    // from the app's own URL) if absent, expand the row, and open the config
    // modal where the bundle is actually authored (no inline editor).
    if (next === 'custom') {
      const nextCustom =
        custom[url] === undefined
          ? { ...custom, [url]: createInitialCredentialData(isWebService, url) }
          : custom;
      updateMulti({ perApp: nextPerApp, custom: nextCustom });
      setExpandedId(url);
      setOpenConfig({ kind: 'custom', url });
      return;
    }
    // Shared / Off just commit the mode — neither opens the modal. Leaving
    // Custom clears the stale expanded strip for this row (F4).
    updateMulti({ perApp: nextPerApp });
    if (expandedId === url) setExpandedId(null);
  };

  const handleSetAll = (mode: 'shared' | 'off') => {
    const nextPerApp: Record<string, (typeof perApp)[string]> = {};
    appUrls.forEach(url => {
      nextPerApp[url] = mode;
    });
    updateMulti({ perApp: nextPerApp });
    // F4: every row becomes shared/off (never custom), so no expanded Custom
    // editor is valid afterward — collapse unconditionally (mirrors the
    // per-row handleModeChange convention at line 86).
    setExpandedId(null);
  };

  // "Configure shared" seeds the shared bundle on first open (it stays null
  // until then so a Shared row reads 'incomplete') and opens the config modal.
  const handleOpenShared = () => {
    if (shared === null) {
      updateMulti({ shared: createInitialCredentialData(isWebService, '') });
    }
    setOpenConfig({ kind: 'shared' });
  };

  const rowStatus = (url: string): AssignmentRowStatus => {
    const mode = perApp[url] ?? 'off';
    if (mode === 'off') return 'na';
    if (mode === 'shared') {
      if (shared === null) return 'incomplete';
      return collectFieldErrors(shared).length ? 'incomplete' : 'ready';
    }
    const data = custom[url];
    if (!data) return 'incomplete';
    return collectFieldErrors(data).length ? 'incomplete' : 'ready';
  };

  // The expanded Custom row renders a one-line summary + a launcher button; the
  // editor itself lives in CredentialConfigModal (no inline authoring).
  const renderEditor = (url: string) => {
    const mode = perApp[url] ?? 'off';
    if (mode !== 'custom') return null;
    const data = custom[url];
    const configured =
      data !== undefined && collectFieldErrors(data).length === 0;
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="text-chariot-text-secondary text-sm">
          {configured ? 'Configured' : 'Needs setup'}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setOpenConfig({ kind: 'custom', url })}
        >
          {configured ? 'Edit credentials' : 'Configure credentials'}
        </Button>
      </div>
    );
  };

  const items = appUrls.map(url => ({
    id: url,
    label: url,
    sublabel: rowStatus(url) === 'incomplete' ? 'Needs setup' : undefined,
  }));
  const modeMap: Record<string, (typeof perApp)[string]> = {};
  appUrls.forEach(url => {
    modeMap[url] = perApp[url] ?? 'off';
  });

  const recorderCount = recorderAppCount(perApp, shared, custom, appUrls);
  const readyCount = appUrls.filter(url => rowStatus(url) === 'ready').length;

  // The bundle the config modal edits: shared → shared, custom → custom[url].
  // May be undefined (modal not yet rendered) — gated at the render site.
  const configBundle =
    openConfig === null
      ? undefined
      : openConfig.kind === 'shared'
        ? (shared ?? undefined)
        : custom[openConfig.url];

  const bulkActions = (
    <div className="flex items-center gap-2">
      <span className="text-chariot-text-secondary text-sm">Set all</span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => handleSetAll('shared')}
      >
        Set all → Shared
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => handleSetAll('off')}
      >
        Set all → Off
      </Button>
    </div>
  );

  const sharedCount = appUrls.filter(
    url => (perApp[url] ?? 'off') === 'shared'
  ).length;

  const sharedSlot = (
    <div className="border-chariot-stroke flex flex-col gap-3 rounded-md border p-4">
      <span className="text-chariot-text-primary text-sm font-medium">
        Shared credentials
      </span>
      {sharedCount > 0 && (
        <span className="text-chariot-text-secondary text-sm">
          {`Used by ${sharedCount} application${sharedCount === 1 ? '' : 's'}`}
        </span>
      )}
      <Button type="button" variant="secondary" onClick={handleOpenShared}>
        <KeyIcon className="size-5" aria-hidden="true" />
        Configure shared credentials
      </Button>
    </div>
  );

  return (
    <div className="flex flex-1 flex-col gap-4">
      {recorderCount > 0 && (
        <InfoCallout
          variant="info"
          aria-live="polite"
          icon={<InformationCircleIcon className="size-5" aria-hidden="true" />}
        >
          <span>{`${recorderCount} ${
            recorderCount === 1 ? 'application uses' : 'applications use'
          } Automated Authentication and will each open a recorder session.`}</span>
        </InfoCallout>
      )}

      <AssignmentList
        items={items}
        mode={modeMap}
        onModeChange={handleModeChange}
        expandedId={expandedId}
        // Selecting another row collapses the prior (accordion-of-one); a
        // re-click that would collapse to null is ignored so a row stays open
        // for editing (matches the auto-expand on Custom selection).
        onExpandedChange={next => next !== null && setExpandedId(next)}
        renderEditor={renderEditor}
        rowStatus={rowStatus}
        sharedSlot={sharedSlot}
        bulkActions={bulkActions}
        // Credential-specific row mode label lives here, not in the generic
        // primitive; keeps AssignmentList domain-agnostic (rendered output
        // unchanged from the previously-hardcoded value).
        modeAriaLabel={item => `Credential mode for ${item.label}`}
        ariaLabel="Per-application credential assignment"
      />

      <div className="flex flex-col gap-1">
        <p className="text-chariot-text-secondary text-sm">
          {`${readyCount} of ${appUrls.length} applications ready`}
        </p>
        <p className="text-chariot-text-secondary text-sm">
          Apps set to No authentication are still added as seeds.
        </p>
      </div>

      {openConfig !== null && configBundle !== undefined && (
        <CredentialConfigModal
          open={openConfig !== null}
          onClose={() => setOpenConfig(null)}
          bundle={configBundle}
          onChange={
            openConfig.kind === 'shared'
              ? next => updateMulti({ shared: next })
              : next =>
                  updateMulti({
                    custom: { ...custom, [openConfig.url]: next },
                  })
          }
          isWebService={isWebService}
          webAppUrl={openConfig.kind === 'shared' ? '' : openConfig.url}
          disabledTypes={
            openConfig.kind === 'shared' ? SHARED_DISABLED_TYPES : undefined
          }
          title={
            openConfig.kind === 'shared'
              ? 'Configure shared credentials'
              : `Configure ${openConfig.url}`
          }
        />
      )}
    </div>
  );
}
